import { TableCell } from '@mui/material';
import { KeyboardEvent, memo } from 'react';
import { Link } from 'react-router-dom';

import {
  convertEvaluationType,
  convertReportGoal,
  convertStatus,
  convertStatusUrl,
  SlaStatus,
  ufAbbreviation,
} from '../../../../api/enumerations';
import { resolveBankDossier } from '../../../../api/workOrders/bankDossier';
import { WorkOrderData } from '../../../../api/workOrders/types';
import { Money } from '../../../../components/UI/Money';
import { StatusPill } from '../../../../components/UI/StatusPill';
import { Constants as DossierConstants } from '../../../../constants/bankDossier';
import { formatDate } from '../../../../helpers';
import {
  BodyRow,
  ClientAvatar,
  ClientCell,
  ClientName,
  ControlCell,
  ControlSecondary,
  DeadlineCell,
  MutedCell,
} from './styles';

type SlaTone = 'overdue' | 'near' | 'ok';

const slaToneFor = (status: SlaStatus | null): SlaTone | null => {
  if (status === SlaStatus.OVERDUE) return 'overdue';
  if (status === SlaStatus.NEAR_DEADLINE) return 'near';
  if (status === SlaStatus.ON_TRACK) return 'ok';
  return null;
};

const slaLabel = (row: WorkOrderData, tone: SlaTone): string => {
  const days = row.sla_days_remaining ?? 0;
  if (tone === 'overdue') {
    const late = Math.abs(days);
    return late === 1 ? '1 dia atrasada' : `${late} dias atrasada`;
  }
  if (tone === 'near') {
    return days === 1 ? 'Vence em 1 dia' : `Vence em ${days} dias`;
  }
  return 'No prazo';
};

const initialsOf = (name: string | null | undefined): string =>
  (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '·';

const formatLocation = (row: WorkOrderData): string => {
  const uf = ufAbbreviation[row.uf];
  if (row.city && uf) return `${row.city} / ${uf}`;
  if (row.city) return row.city;
  if (uf) return uf;
  return '—';
};

const renderDeadline = (row: WorkOrderData): JSX.Element => {
  const tone = slaToneFor(row.sla_status);
  const date = row.limit_finish_at ? formatDate(row.limit_finish_at) : null;
  // "No prazo" is the normal state and stays silent (guide 7.5).
  return (
    <DeadlineCell>
      {date && <span>{date}</span>}
      {tone === 'overdue' && (
        <StatusPill tone="negative" dot>
          {slaLabel(row, tone)}
        </StatusPill>
      )}
      {tone === 'near' && (
        <StatusPill tone="warning" dot>
          {slaLabel(row, tone)}
        </StatusPill>
      )}
      {!date && !tone && <span>—</span>}
    </DeadlineCell>
  );
};

interface WorkOrderRowProps {
  row: WorkOrderData;
  onOpen: (row: WorkOrderData) => void;
}

function WorkOrderRowComponent({
  row,
  onOpen,
}: WorkOrderRowProps): JSX.Element {
  const detailUrl = `/home/property/${row.id}/${convertStatusUrl(row.status)}`;
  const dossier = resolveBankDossier(row);

  const handleKeyDown = (e: KeyboardEvent<HTMLTableRowElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(row);
    }
  };

  return (
    <BodyRow
      role="link"
      tabIndex={0}
      aria-label={`OS ${row.reference_number}, ${
        row.client_name || 'sem cliente'
      }, ${convertStatus(row.status)}`}
      onClick={() => onOpen(row)}
      onKeyDown={handleKeyDown}
    >
      <TableCell>
        <Link to={detailUrl} onClick={(e) => e.stopPropagation()}>
          {row.reference_number}
        </Link>
      </TableCell>
      <TableCell>
        <ClientCell>
          <ClientAvatar aria-hidden="true">
            {initialsOf(row.client_name)}
          </ClientAvatar>
          <ClientName title={row.client_name || undefined}>
            {row.client_name || '—'}
          </ClientName>
        </ClientCell>
      </TableCell>
      <TableCell>
        <ControlCell>
          <span>{dossier?.identification.bank_control_number ?? '—'}</span>
          <ControlSecondary>
            {DossierConstants.list.folder}{' '}
            {dossier?.identification.folder_number ?? '—'}
          </ControlSecondary>
        </ControlCell>
      </TableCell>
      <TableCell>
        <StatusPill tone="neutral">{convertStatus(row.status)}</StatusPill>
      </TableCell>
      <MutedCell>{row.worker_company?.name || '—'}</MutedCell>
      <MutedCell>{row.inspection?.engineer_name || '—'}</MutedCell>
      <MutedCell>{formatLocation(row)}</MutedCell>
      <MutedCell>
        {row.report_type_label ||
          convertEvaluationType(row.evaluation_type) ||
          '—'}
      </MutedCell>
      <MutedCell>{convertReportGoal(row.report_goal) || '—'}</MutedCell>
      <MutedCell>
        {row.inspection?.schedule_at
          ? formatDate(row.inspection.schedule_at)
          : '—'}
      </MutedCell>
      <TableCell>{renderDeadline(row)}</TableCell>
      <TableCell align="right">
        <Money value={dossier?.appraisal_value ?? null} />
      </TableCell>
    </BodyRow>
  );
}

/** Memoized: filtering or sorting the table must not re-render untouched rows. */
export const WorkOrderRow = memo(WorkOrderRowComponent);
